import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  makeStyles,
  Popover,
  Typography,
} from '@material-ui/core'
import Icon from '@mdi/react'
import {
  mdiBellOffOutline,
  mdiBellOutline,
  mdiBellRingOutline,
  mdiCheckAll,
  mdiChevronDown,
  mdiChevronUp,
  mdiClockOutline,
} from '@mdi/js'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import colors from '../../../assets/colors'
import { hasRole } from '../../../util/utils'
import { formatDateTime } from '../../alerts/pages/helpers'
import {
  fetchLaundryAlerts,
  fetchUnread,
  readAlert,
  readAllAlerts,
} from '../../laundry-alerts/LaundryAlertsActions'

const ROWS = 10

const TAB_UNREAD = 0
const TAB_ALL = 1

const useStyles = makeStyles({
  // Balança várias vezes e fica um tempo parado antes do próximo ciclo
  '@keyframes bellRing': {
    '0%': { transform: 'rotate(0)' },
    '4%': { transform: 'rotate(16deg)' },
    '8%': { transform: 'rotate(-14deg)' },
    '12%': { transform: 'rotate(14deg)' },
    '16%': { transform: 'rotate(-12deg)' },
    '20%': { transform: 'rotate(12deg)' },
    '24%': { transform: 'rotate(-10deg)' },
    '28%': { transform: 'rotate(8deg)' },
    '32%': { transform: 'rotate(-6deg)' },
    '36%': { transform: 'rotate(4deg)' },
    '40%': { transform: 'rotate(-2deg)' },
    '44%, 100%': { transform: 'rotate(0)' },
  },
  '@keyframes liquidWave': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  bellButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '13px',
    color: '#FFF',
    height: '32px',
    padding: '0 12px',
    marginLeft: '4px',
    borderRadius: '8px',
    backgroundColor: colors.primary,
    '&:hover': {
      backgroundColor: colors.primary,
      opacity: 0.9,
    },
  },
  // Água subindo por baixo (onda girando), diferente do gradiente do Assistente IA.
  // borderRadius perto de 50% = onda mais baixa; duração maior = mais lenta
  bellButtonUnread: {
    overflow: 'hidden',
    '& .MuiButton-label': {
      position: 'relative',
      zIndex: 1,
    },
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      left: '-75%',
      width: '250%',
      paddingTop: '250%',
      pointerEvents: 'none',
    },
    '&::before': {
      top: '65%',
      borderRadius: '46%',
      background: colors.secondary,
      opacity: 0.5,
      animation: '$liquidWave 12s linear infinite',
    },
    '&::after': {
      top: '75%',
      borderRadius: '45%',
      background: '#2679be',
      opacity: 0.85,
      animation: '$liquidWave 9s linear infinite reverse',
    },
  },
  bellIcon: {
    color: '#FFF',
  },
  bellIconRinging: {
    transformOrigin: '50% 10%',
    animation: '$bellRing 4s ease-in-out infinite',
  },
  bellCount: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    marginLeft: '8px',
    padding: '0 6px',
    borderRadius: '6px',
    background: '#FFF',
    color: colors.primary,
    fontSize: '12px',
    fontWeight: 700,
  },
  // Popover no padrão dos modais do sistema (ex.: status da assinatura)
  paper: {
    width: 400,
    maxWidth: 'calc(100vw - 32px)',
    marginTop: 8,
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0, 11, 65, 0.16)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 16px 12px',
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.primary,
  },
  headerBadge: {
    backgroundColor: colors.bgInfo,
    color: colors.info,
    fontSize: '12px',
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
  },
  segmented: {
    display: 'flex',
    gap: 4,
    margin: '0 16px 12px',
    padding: 4,
    borderRadius: 10,
    backgroundColor: colors.lightGray,
  },
  segment: {
    flex: 1,
    height: 30,
    borderRadius: 8,
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 600,
    color: colors.textGray,
    '&:hover': {
      backgroundColor: 'rgba(0, 11, 65, 0.06)',
    },
  },
  segmentActive: {
    backgroundColor: colors.primary,
    color: '#FFF',
    '&:hover': {
      backgroundColor: colors.primary,
      opacity: 0.9,
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 400,
    padding: '0 16px',
    overflowY: 'auto',
  },
  card: {
    flexShrink: 0,
    border: `1px solid ${colors.graylight}`,
    borderRadius: 10,
    backgroundColor: '#FFF',
    overflow: 'hidden',
    transition: 'border-color .2s ease-in-out',
    '&:hover': {
      borderColor: colors.grayMedium,
    },
  },
  cardUnread: {
    borderColor: 'rgba(83, 159, 221, 0.5)',
    backgroundColor: 'rgba(83, 159, 221, 0.06)',
    '&:hover': {
      borderColor: colors.secondary,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    cursor: 'pointer',
  },
  cardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: colors.lightGray,
    color: colors.textPrimary,
  },
  cardIconUnread: {
    backgroundColor: colors.primary,
    color: '#FFF',
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: 14,
    color: colors.primary,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  cardTitleUnread: {
    fontWeight: 700,
  },
  cardDate: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
    fontSize: 12,
    color: colors.textPrimary,
  },
  newTag: {
    marginLeft: 6,
    padding: '1px 6px',
    borderRadius: 6,
    backgroundColor: colors.secondary,
    color: '#FFF',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  chevron: {
    flexShrink: 0,
    color: colors.textPrimary,
  },
  // Texto vem do Quill, sanitizado no backend
  cardText: {
    margin: '0 12px 12px',
    paddingTop: 12,
    borderTop: `1px solid ${colors.graylight}`,
    fontSize: 13,
    lineHeight: 1.5,
    color: colors.textGray,
    wordBreak: 'break-word',
    '& p': {
      margin: '0 0 6px',
    },
    '& ul, & ol': {
      margin: '0 0 6px',
      paddingLeft: 20,
    },
    '& a': {
      color: colors.secondary,
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: 6,
    },
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '32px 16px',
    color: colors.grayMedium,
  },
  emptyText: {
    fontSize: 14,
    color: colors.textPrimary,
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    padding: 8,
  },
  progress: {
    color: colors.primary,
  },
  loadMore: {
    flexShrink: 0,
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 600,
    color: colors.secondary,
  },
  footer: {
    padding: 16,
  },
  primaryButton: {
    width: '100%',
    backgroundColor: colors.primary,
    color: '#FFF',
    borderRadius: '10px',
    padding: '10px',
    fontWeight: 700,
    textTransform: 'none',
    transition: 'all .4s ease-in-out',
    '&:hover': {
      backgroundColor: colors.primary,
      opacity: 0.85,
    },
    '&:disabled': {
      backgroundColor: colors.primary,
      color: '#FFF',
      opacity: 0.4,
    },
  },
})

const LaundryAlertsBell = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const roles = useSelector(state => state.auth.role)
  const { unreadCount, items, meta } = useSelector(state => state.laundryAlerts)

  const [anchorEl, setAnchorEl] = useState(null)
  const [tab, setTab] = useState(TAB_UNREAD)
  const [expandedId, setExpandedId] = useState(null)
  const [loading, setLoading] = useState(false)

  const isManager = hasRole('gerente_lavanderia', roles)

  useEffect(() => {
    if (!isManager) return

    dispatch(fetchUnread())
  }, [roles])

  const loadAlerts = (currentTab, page = 1) => {
    setLoading(true)
    dispatch(
      fetchLaundryAlerts({
        rows: ROWS,
        page,
        // String: o removeEmpty descartaria o 0 numérico
        read: currentTab === TAB_UNREAD ? '0' : '',
      })
    ).finally(() => setLoading(false))
  }

  const handleOpen = event => {
    const initialTab = unreadCount > 0 ? TAB_UNREAD : TAB_ALL

    setAnchorEl(event.currentTarget)
    setTab(initialTab)
    setExpandedId(null)
    loadAlerts(initialTab)
    dispatch(fetchUnread())
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleChangeTab = value => {
    if (value === tab) return

    setTab(value)
    setExpandedId(null)
    loadAlerts(value)
  }

  const handleToggle = alert => {
    const isExpanding = expandedId !== alert.id

    setExpandedId(isExpanding ? alert.id : null)
    if (isExpanding && !alert.read) dispatch(readAlert(alert.id))
  }

  const handleReadAll = () => {
    dispatch(readAllAlerts())
  }

  const { current_page: currentPage = 1, last_page: lastPage = 1 } = meta || {}

  const handleLoadMore = () => {
    loadAlerts(tab, currentPage + 1)
  }

  if (!isManager) return null

  const hasMore = currentPage < lastPage

  return (
    <>
      <Button
        className={`${classes.bellButton} ${
          unreadCount ? classes.bellButtonUnread : ''
        }`}
        onClick={handleOpen}
        aria-label="Abrir os alertas"
        startIcon={
          <Icon
            path={unreadCount ? mdiBellRingOutline : mdiBellOutline}
            size={0.75}
            className={`${classes.bellIcon} ${
              unreadCount ? classes.bellIconRinging : ''
            }`}
          />
        }
      >
        Alertas
        {!!unreadCount && (
          <span className={classes.bellCount}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.paper }}
      >
        <Box className={classes.header}>
          <Typography className={classes.title}>Alertas</Typography>
          {!!unreadCount && (
            <Typography component="span" className={classes.headerBadge}>
              {unreadCount} não {unreadCount === 1 ? 'lido' : 'lidos'}
            </Typography>
          )}
        </Box>

        <Box className={classes.segmented}>
          <Button
            className={`${classes.segment} ${
              tab === TAB_UNREAD ? classes.segmentActive : ''
            }`}
            onClick={() => handleChangeTab(TAB_UNREAD)}
          >
            Não lidos
          </Button>
          <Button
            className={`${classes.segment} ${
              tab === TAB_ALL ? classes.segmentActive : ''
            }`}
            onClick={() => handleChangeTab(TAB_ALL)}
          >
            Todos
          </Button>
        </Box>

        <Box className={classes.list}>
          {!items.length && !loading && (
            <Box className={classes.empty}>
              <Icon path={mdiBellOffOutline} size={1.6} />
              <Typography className={classes.emptyText}>
                {tab === TAB_UNREAD
                  ? 'Você está em dia com os alertas'
                  : 'Nenhum alerta recebido'}
              </Typography>
            </Box>
          )}

          {items.map(alert => {
            const isExpanded = expandedId === alert.id

            return (
              <Box
                key={alert.id}
                className={`${classes.card} ${
                  alert.read ? '' : classes.cardUnread
                }`}
              >
                <Box
                  className={classes.cardHeader}
                  onClick={() => handleToggle(alert)}
                >
                  <Box
                    className={`${classes.cardIcon} ${
                      alert.read ? '' : classes.cardIconUnread
                    }`}
                  >
                    <Icon
                      path={alert.read ? mdiBellOutline : mdiBellRingOutline}
                      size={0.8}
                    />
                  </Box>
                  <Box className={classes.cardInfo}>
                    <Typography
                      className={`${classes.cardTitle} ${
                        alert.read ? '' : classes.cardTitleUnread
                      }`}
                    >
                      {alert.title}
                    </Typography>
                    <Typography component="div" className={classes.cardDate}>
                      <Icon path={mdiClockOutline} size={0.5} />
                      {formatDateTime(alert.received_at)}
                      {!alert.read && (
                        <span className={classes.newTag}>Novo</span>
                      )}
                    </Typography>
                  </Box>
                  <Icon
                    className={classes.chevron}
                    path={isExpanded ? mdiChevronUp : mdiChevronDown}
                    size={0.8}
                  />
                </Box>

                <Collapse in={isExpanded} unmountOnExit>
                  <Box
                    className={classes.cardText}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: alert.text }}
                  />
                </Collapse>
              </Box>
            )
          })}

          {loading && (
            <Box className={classes.loadingBox}>
              <CircularProgress size={20} className={classes.progress} />
            </Box>
          )}

          {!loading && hasMore && (
            <Button className={classes.loadMore} onClick={handleLoadMore}>
              Carregar mais
            </Button>
          )}
        </Box>

        <Box className={classes.footer}>
          <Button
            className={classes.primaryButton}
            disabled={!unreadCount}
            onClick={handleReadAll}
            startIcon={<Icon path={mdiCheckAll} size={0.8} />}
          >
            Marcar todos como lidos
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default LaundryAlertsBell
